import { toast } from "sonner";
import { Download, FileText, Image as ImageIcon } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, PRESS_QUOTES } from "@/data/content";
import { Reveal, ChapterLabel, PageHero, ImageReveal, usePageTitle } from "@/components/Shared";
import { PressArticles } from "@/components/PressArticles";

const PRESS_PHOTOS = [IMG.press, "/images/press-2.webp", IMG.hero, "/images/press-4.webp", IMG.coaching, "/images/press-6.webp"];

const PressPage = () => {
  const { t, lf, lang } = useLang();
  usePageTitle(`${t.nav.press} — ${ARTIST_NAME}`);

  const handleZip = () => {
    toast(lang === "de" ? "Press Kit wird heruntergeladen …" : "Downloading press kit …");
    const a = document.createElement("a");
    a.href = "/press/press-kit.zip";
    a.download = "press-kit.zip";
    a.click();
  };

  return (
    <div data-testid="press-page">
      <PageHero eyebrow={t.press.eyebrow} title={t.press.title} img={IMG.press} imgClass="object-[60%_20%]">
        <Reveal delay={0.4}>
          <p className="font-sans text-sm leading-relaxed text-[#A1A1AA]">{t.press.intro}</p>
        </Reveal>
      </PageHero>

      {/* Downloads */}
      <section className="px-6 py-20 sm:px-12 sm:py-28 lg:px-20">
        <Reveal><ChapterLabel no="01" label={t.press.kitLabel} /></Reveal>
        <div data-testid="press-downloads-list" className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {t.press.downloads.map((d, i) => (
            <Reveal key={d.file} delay={i * 0.05}>
              <a
                href={d.file}
                download
                data-testid={`press-download-${i}`}
                data-cursor="OPEN"
                className="group flex items-center justify-between gap-4 border border-white/10 px-6 py-5 transition-all duration-400 hover:border-[#C5A059]/50 hover:bg-white/[0.02]"
              >
                <span className="flex items-center gap-4">
                  <FileText className="h-4 w-4 shrink-0 text-[#C5A059]" />
                  <span className="font-sans text-sm text-[#F9F8F6] transition-colors group-hover:text-[#C5A059]">{d.name}</span>
                </span>
                <Download className="h-4 w-4 shrink-0 text-[#71717A] transition-all duration-300 group-hover:translate-y-0.5 group-hover:text-[#C5A059]" />
              </a>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-10">
          <button
            data-testid="press-download-kit-button"
            data-cursor="OPEN"
            onClick={handleZip}
            className="inline-flex items-center gap-4 bg-[#C5A059] px-10 py-4 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#0B0B0C] transition-colors duration-400 hover:bg-[#E5C158]"
          >
            <Download className="h-4 w-4" />
            {t.press.downloadAll}
          </button>
        </Reveal>
      </section>

      {/* Press Photos */}
      <section className="border-t border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="02" label={t.press.photosLabel} /></Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRESS_PHOTOS.map((p, i) => (
            <div key={p} data-cursor="VIEW">
              <ImageReveal src={p} alt={`Press ${i + 1}`} bw={i === 2} delay={i * 0.06} className="aspect-[4/3]" imgClass={i === 1 ? "object-[50%_22%]" : i === 4 ? "object-[60%_30%]" : i === 5 ? "object-[50%_18%]" : ""}>
                <div className="absolute inset-0 flex items-end justify-between bg-gradient-to-t from-[#0B0B0C]/80 to-transparent p-4 opacity-0 transition-opacity duration-500 group-hover/ir:opacity-100">
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#F9F8F6]">Photo 0{i + 1} · © {ARTIST_NAME}</span>
                  <ImageIcon className="h-4 w-4 text-[#C5A059]" />
                </div>
              </ImageReveal>
            </div>
          ))}
        </div>
      </section>

      {/* Press articles */}
      <PressArticles />

      {/* Quotes + Publications */}
      <section className="border-t border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><ChapterLabel no="04" label={t.press.quotesLabel} /></Reveal>
            <div className="mt-10 space-y-10">
              {PRESS_QUOTES.map((q) => (
                <Reveal key={q.id}>
                  <blockquote className="border-l border-[#C5A059]/40 pl-6">
                    <p className="font-serif text-xl font-light italic leading-relaxed text-[#F9F8F6]/90 sm:text-2xl">{lf(q.text)}</p>
                    <cite className="mt-3 block font-mono text-[10px] uppercase not-italic tracking-[0.3em] text-[#71717A]">— {q.source}</cite>
                  </blockquote>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal><ChapterLabel no="05" label={t.press.pubsLabel} /></Reveal>
            <ul className="mt-10 space-y-4">
              {t.press.pubs.map((p) => (
                <li key={p} className="border-b border-white/5 pb-4 font-sans text-sm text-[#A1A1AA]">{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressPage;
